/**
 * Global Error Tracker — captures frontend errors and reports to admin panel.
 * Captures: React crashes, API errors (4xx/5xx), unhandled exceptions, console.error
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce/dedup: don't spam the same error
const recentErrors = new Set();
const DEDUP_TTL = 10000; // 10s

const isDuplicate = (key) => {
  if (recentErrors.has(key)) return true;
  recentErrors.add(key);
  setTimeout(() => recentErrors.delete(key), DEDUP_TTL);
  return false;
};

const getToken = () => localStorage.getItem('rollm8s_token') || localStorage.getItem('token');

/**
 * Send error report to backend
 */
const sendReport = async (report) => {
  const key = `${report.error_type}:${report.message}`.slice(0, 200);
  if (isDuplicate(key)) return;

  try {
    const headers = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch(`${API}/error-reports`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        error_type: report.error_type,
        message: report.message,
        stack_trace: report.stack_trace || '',
        page_url: window.location.href,
        component_stack: report.component_stack || '',
        user_agent: navigator.userAgent,
        extra: report.extra || {},
      }),
    });
  } catch {
    // Silently fail — we don't want error reporting to cause more errors
  }
};

/**
 * Report a React ErrorBoundary crash
 */
export const reportReactCrash = (error, errorInfo) => {
  sendReport({
    error_type: 'react_crash',
    message: error?.message || String(error),
    stack_trace: error?.stack || '',
    component_stack: errorInfo?.componentStack || '',
  });
};

/**
 * Report an API error (4xx/5xx)
 */
export const reportApiError = (error) => {
  const status = error?.response?.status;
  // Only report 5xx and unexpected 4xx (skip 401 on auth pages, 404 on optional fetches)
  if (status === 401 || status === 404) return;

  sendReport({
    error_type: 'api_error',
    message: `${error?.config?.method?.toUpperCase()} ${error?.config?.url} → ${status} ${error?.response?.statusText || ''}`.trim(),
    stack_trace: error?.stack || '',
    extra: {
      status_code: status,
      request_url: error?.config?.url,
      request_method: error?.config?.method,
      response_data: typeof error?.response?.data === 'string'
        ? error.response.data.slice(0, 500)
        : JSON.stringify(error?.response?.data || {}).slice(0, 500),
    },
  });
};

/**
 * Install global error listeners
 */
export const installGlobalErrorTracking = () => {
  // 1. Unhandled JS errors
  window.addEventListener('error', (event) => {
    // Ignore cross-origin script errors (no useful info)
    if (event.message === 'Script error.' && !event.filename) return;
    sendReport({
      error_type: 'unhandled_exception',
      message: event.message || 'Unknown error',
      stack_trace: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  // 2. Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    sendReport({
      error_type: 'unhandled_exception',
      message: reason?.message || String(reason),
      stack_trace: reason?.stack || '',
    });
  });

  // 3. Console.error interception
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);

    // Skip React internal warnings/dev messages
    const msg = args.map(a => (typeof a === 'string' ? a : (a?.message || ''))).join(' ');
    if (
      msg.includes('Warning:') ||
      msg.includes('React does not recognize') ||
      msg.includes('validateDOMNesting') ||
      msg.includes('Each child in a list should have a unique') ||
      msg.length < 10
    ) return;

    sendReport({
      error_type: 'console_error',
      message: msg.slice(0, 500),
      stack_trace: new Error().stack || '',
    });
  };
};

/**
 * Install axios response interceptor for API error tracking
 */
export const installAxiosInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      reportApiError(error);
      return Promise.reject(error);
    }
  );
};

export default { reportReactCrash, reportApiError, installGlobalErrorTracking };
